import { useMemo } from "react";
import { NOT_CLIENT_PICKED, canonicalFeeKeys, listCommissionTypes, normalizeFees, parseValue, type CommissionTypeOption } from "@shared/commission";
import type { BackgroundType, ContentRating, IntakeArtist } from "../types";

export interface ArtistOptions {
  types: CommissionTypeOption[]; fees: Record<string, string>; backgrounds: BackgroundType[];
  maxCharacters: number; ratingOptions: ContentRating[]; manualFeeKeys: string[];
}

const AUTO_OR_DERIVED = new Set(["background_simple", "background_complex", "extra_character", "commercial_use", "nsfw_content"]);

export function artistOptions(a: IntakeArtist): ArtistOptions {
  const fees = normalizeFees(a.prices, a.additionalFees);
  const offered = (k: string) => !(k in fees) || parseValue(fees[k]).kind !== "dont";
  const backgrounds: BackgroundType[] = ["none"];
  if (offered("background_simple")) backgrounds.push("simple");
  if (offered("background_complex")) backgrounds.push("complex");
  const ratingOptions: ContentRating[] =
    a.nsfwLevel === "nsfw" ? ["sfw", "suggestive", "nsfw"] : a.nsfwLevel === "suggestive" ? ["sfw", "suggestive"] : ["sfw"];
  let manualFeeKeys = Object.keys(fees).filter((k) => !AUTO_OR_DERIVED.has(k) && !NOT_CLIENT_PICKED.has(k) && parseValue(fees[k]).kind !== "dont");
  // visible_fees holds human labels ("Rush Order") in the live DB; normalize before comparing.
  const visible = new Set(a.visibleFees.flatMap(canonicalFeeKeys));
  if (visible.size > 0) manualFeeKeys = manualFeeKeys.filter((k) => visible.has(k));
  return {
    types: listCommissionTypes(a.prices), fees, backgrounds,
    maxCharacters: "extra_character" in fees && parseValue(fees.extra_character).kind === "dont" ? 1 : 5,
    ratingOptions, manualFeeKeys,
  };
}

export function useArtistOptions(a: IntakeArtist | undefined): ArtistOptions | null {
  return useMemo(() => (a ? artistOptions(a) : null), [a]);
}
